import { c as _c } from "react/compiler-runtime";
import { Formik } from 'formik';
import Head from 'next/head';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import useSWR from 'swr';
import { APITeam, APITeamWithRole } from '../../../../../../typings/Team.interface';
import { getMFASetupUrl } from '../../../client/Hookdeck';
import APIMethodKeys from '../../../client/APIMethodKeys';
import Alert from '../../common/base/Alert';
import Badge from '../../common/base/Badge';
import Button from '../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../common/base/Card';
import Container from '../../common/base/Container';
import Icon from '../../common/base/Icon';
import Loading from '../../common/base/Loading';
import Text from '../../common/base/Text';
import { ClickableArea } from '../../common/base/Button';
import Dropdown from '../../common/Dropdown';
import { Div } from '../../common/helpers/StyledUtils';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import FullscreenLayout from '../../layouts/FullscreenLayout';
import TeamSelectionList from './Nav/TeamSelection/TeamSelectionList';
import NewOrganizationModal from './Nav/TeamSelection/NewOrganization';
import NewTeamModal from './Nav/TeamSelection/NewTeam';
import OnboardingNav from './Onboarding/OnboardingNav';
import { DashboardContext } from './DashboardContext';
interface FormValues {
  team_id: string;
}
const CliAuthView: React.FC = () => {
  const $ = _c(54);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const history = useHistory();
  const location = useLocation();
  const [key_claimed, setKeyClaimed] = useState(false);
  const [error, setError] = useState(null);
  const {
    team
  } = useContext(DashboardContext);
  const [selected_team, setSelectedTeam] = useState(team || null);
  const match = useRouteMatch();
  const [modal, setModal] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.teams.listAll();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.teams) {
    t1 = () => HookdeckAPI.teams.listAll();
    $[1] = HookdeckAPI.teams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: teams,
    mutate: mutateTeams,
    isValidating: loading_teams
  } = useSWR(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.organizations.listAll();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.organizations) {
    t3 = () => HookdeckAPI.organizations.listAll();
    $[4] = HookdeckAPI.organizations;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: organizations,
    mutate: mutateOrganizations,
    isValidating: loading_organizations
  } = useSWR(t2, t3);
  const loading = loading_teams || loading_organizations;
  const needs_onboarding_before_claim = !key_claimed && !loading && (teams?.length ?? 0) === 0;
  let t4;
  let t5;
  if ($[6] !== history || $[7] !== location.pathname || $[8] !== location.search || $[9] !== needs_onboarding_before_claim) {
    t4 = () => {
      if (!needs_onboarding_before_claim) {
        return;
      }
      const return_path = `${location.pathname}${location.search}`;
      history.replace(`/onboarding?redirect=${encodeURIComponent(return_path)}`);
    };
    t5 = [needs_onboarding_before_claim, history, location.pathname, location.search];
    $[6] = history;
    $[7] = location.pathname;
    $[8] = location.search;
    $[9] = needs_onboarding_before_claim;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = team_for_display => {
      if (team_for_display) {
        setSelectedTeam(team_for_display);
      }
      setKeyClaimed(true);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const completeClaim = t6;
  let t7;
  if ($[13] !== addToast || $[14] !== mutateOrganizations || $[15] !== mutateTeams) {
    t7 = async team_0 => {
      await Promise.all([mutateOrganizations(), mutateTeams()]);
      setSelectedTeam(team_0);
      setModal(null);
      addToast("success", `Project ${team_0.name} created and selected for CLI`);
    };
    $[13] = addToast;
    $[14] = mutateOrganizations;
    $[15] = mutateTeams;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleTeamCreated = t7;
  let t8;
  if ($[17] !== HookdeckAPI.session || $[18] !== match.params.key || $[19] !== selected_team || $[20] !== teams) {
    t8 = values => {
      HookdeckAPI.session.claimCliAuthToken(match.params.key, values.team_id).then(() => {
        const claimed_team = teams?.find(t => t.id === values.team_id) || selected_team;
        completeClaim(claimed_team || null);
      }).catch(err => {
        const mfa_setup_url = getMFASetupUrl(err);
        if (mfa_setup_url) {
          window.location.assign(mfa_setup_url);
        } else {
          if (err?.status === 404) {
            setError("This authentication link is invalid or has expired. Please run \"hookdeck login\" again to generate a new authentication link.");
          } else {
            if (err?.status === 401) {
              setError("Authentication failed. Please ensure you are logged in and try again.");
            } else {
              setError("Unable to authenticate your CLI. Please try running \"hookdeck login\" again or contact support if the issue persists.");
            }
          }
        }
      });
    };
    $[17] = HookdeckAPI.session;
    $[18] = match.params.key;
    $[19] = selected_team;
    $[20] = teams;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleSubmit = t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (team_1, toggle) => {
      setSelectedTeam(team_1);
      toggle(false);
    };
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleTeamSelect = t9;
  const t10 = selected_team?.id || "";
  let t11;
  if ($[23] !== t10) {
    t11 = {
      team_id: t10
    };
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const initial_values = t11;
  const show_loading = loading || needs_onboarding_before_claim;
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Head><meta name="referrer" content="no-referrer" /></Head>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== handleTeamCreated || $[27] !== modal?.type) {
    t13 = modal?.type === "organization" && <NewOrganizationModal onClose={() => setModal(null)} onSuccess={handleTeamCreated} />;
    $[26] = handleTeamCreated;
    $[27] = modal?.type;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== handleTeamCreated || $[30] !== modal) {
    t14 = modal?.type === "project" && modal.organization_id && <NewTeamModal organization_id={modal.organization_id} onClose={() => setModal(null)} onSuccess={handleTeamCreated} />;
    $[29] = handleTeamCreated;
    $[30] = modal;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <OnboardingNav />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      justify: "center",
      align: "center",
      direction: "column"
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== key_claimed) {
    t17 = key_claimed ? <Div m={{
      b: 6
    }}><Div flex={{
        align: "center",
        justify: "center"
      }}><Icon icon="success_circle" large={true} left={true} success={true} /><Text bold={true} size="2xl">CLI authenticated</Text></Div><Text muted={true} m={{
        t: 1
      }}>Hookdeck CLI has successfully connected to your project.</Text></Div> : <Div m={{
      b: 6
    }}><Text bold={true} size="2xl">Connect Hookdeck CLI</Text><Text muted={true} m={{
        t: 1
      }}>Confirm the project you'd like to connect the CLI with.</Text></Div>;
    $[34] = key_claimed;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== error) {
    t18 = error && <Div m={{
      b: 2
    }} w={100}><Alert inline={true} danger={true}>{error}</Alert></Div>;
    $[36] = error;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== handleSubmit || $[39] !== initial_values || $[40] !== key_claimed || $[41] !== organizations || $[42] !== selected_team || $[43] !== show_loading || $[44] !== teams) {
    t19 = <StyledCard raised={true} overflow_hidden={true} p={4}>{key_claimed ? <Div flex={{
        justify: "center",
        align: "center",
        direction: "column",
        gap: 4
      }}><Button p={{
          r: 1.5
        }} outline={true} large={true} block={true} style={{
          justifyContent: "space-between"
        }}><Text ellipsis={true}><Icon icon="folder" left={true} />{selected_team?.name}</Text><Badge success={true} subtle={true}><Icon icon="done_all" left={true} success={true} />CLI Connected</Badge></Button><Button to="/" block={true} primary={true}>Go to project</Button></Div> : show_loading ? <Loading /> : <Formik initialValues={initial_values} enableReinitialize={true} validate={_temp} onSubmit={handleSubmit}>{props => <form onSubmit={props.handleSubmit}><Div m={{
            b: 4
          }}><Dropdown block={true} outline={true} placement="bottom-start" renderToggle={(opened, toggle_0) => <Button outline={true} large={true} block={true} onClick={() => toggle_0(!opened)} style={{
              justifyContent: "space-between"
            }}><Text ellipsis={true} muted={!selected_team} danger={props.submitCount > 0 && !!props.errors.team_id}>{selected_team && <Icon icon="folder" left={true} />}{selected_team?.name || "Select a project..."}</Text><Icon icon="expand_all" muted={!selected_team} /></Button>}>{toggle_1 => <TeamSelectionList organizations={organizations} teams={teams} selected_team_id={selected_team?.id} onTeamSelect={team_2 => {
                handleTeamSelect(team_2, toggle_1);
                props.setFieldValue("team_id", team_2.id);
              }} footer={organizations && organizations.length > 0 ? t20 => {
                const {
                  focused_organization_id
                } = t20;
                return {
                  organizations: <StyledCardSection m={{
                    t: "auto"
                  }} p={1}><ClickableArea onClick={() => setModal({
                      type: "organization"
                    })} p={{
                      y: 1.5,
                      x: 2
                    }} rounded={true} flex={{
                      align: "center"
                    }}><Icon icon="add_circle" muted={true} left={true} pointer={true} /><Text>Create organization</Text></ClickableArea></StyledCardSection>,
                  teams: focused_organization_id ? <StyledCardSection m={{
                    t: "auto"
                  }} p={1}><ClickableArea onClick={() => setModal({
                      type: "project",
                      organization_id: focused_organization_id
                    })} p={{
                      y: 1.5,
                      x: 2
                    }} rounded={true} flex={{
                      align: "center"
                    }}><Icon icon="add_circle" muted={true} left={true} pointer={true} /><Text>New project</Text></ClickableArea></StyledCardSection> : null
                };
              } : undefined} />}</Dropdown>{props.submitCount > 0 && props.errors.team_id && <Text m={{
              t: 1,
              b: 0
            }} size="s" as="p" danger={true}><Icon icon="info" left={1} />{props.errors.team_id}</Text>}</Div><Button block={true} primary={true} submit={true}>Connect CLI</Button></form>}</Formik>}</StyledCard>;
    $[38] = handleSubmit;
    $[39] = initial_values;
    $[40] = key_claimed;
    $[41] = organizations;
    $[42] = selected_team;
    $[43] = show_loading;
    $[44] = teams;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t17 || $[47] !== t18 || $[48] !== t19) {
    t20 = <FullscreenLayout center={true}>{t15}<Container small={true} stretch={true} flex={t16}>{t17}{t18}{t19}</Container></FullscreenLayout>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t13 || $[51] !== t14 || $[52] !== t20) {
    t21 = <>{t12}{t13}{t14}{t20}</>;
    $[50] = t13;
    $[51] = t14;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  return t21;
};
export default CliAuthView;
function _temp(values_0) {
  const errors = {};
  if (!values_0.team_id) {
    errors.team_id = "Please select a project";
  }
  return errors;
}